import { useState, useRef, useEffect, useCallback } from "react";
import { Redirect, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { stripWhatsApp, getPublicHost } from "@/lib/utils";
import { abreWhatsApp } from "@/lib/abreWhatsApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import {
  Loader2, ArrowRight, ArrowLeft, Check, ShoppingBag,
  Calendar, Layers, Upload, Clock, User as UserIcon, Copy,
  ExternalLink, PartyPopper, ImagePlus, X, MessageCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import { customFetch, storeSessionId } from "@workspace/api-client-react";
import { getTrackingData, trackCompleteRegistration } from "@/hooks/use-tracking";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getGetMeQueryKey } from "@workspace/api-client-react";
import { requestCodeFlow, CodeLoginCard, getApiErrorMessage } from "@/components/CodeLoginCard";
import { CODIGO_CHEGA_EM_30S } from "@/lib/avisoDoCodigo";

import { capitalizeWords, formatNameForSave } from "@workspace/text";

function generateSlug(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/(^-|-$)/g, "");
}

const dayLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const timeOptions: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    timeOptions.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}
timeOptions.push("23:59");

interface TimeRange { start: string; end: string; }
interface DaySchedule { enabled: boolean; ranges: TimeRange[]; }
type ServiceSchedule = Record<string, DaySchedule>;

function formatCurrency(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  const num = parseInt(digits, 10) / 100;
  return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseCurrency(formatted: string): number {
  const digits = formatted.replace(/\D/g, "");
  if (!digits) return 0;
  return parseInt(digits, 10) / 100;
}

export default function Register() {
  const [step, setStep] = useState(1);
  const [, setLocation] = useLocation();
  const { user, isLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [ddi, setDdi] = useState("55");
  const [whatsappValidating, setWhatsappValidating] = useState(false);
  const [whatsappError, setWhatsappError] = useState("");
  const [confirmingWhatsapp, setConfirmingWhatsapp] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState<{ whatsappFormattedNumber: string | null } | null>(null);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState("");
  const [codeRequest, setCodeRequest] = useState<{
    requestId: string;
    whatsappFormattedNumber: string;
    whatsappNumber: string;
    ddi: string;
  } | null>(null);
  const [whatsappConfirm, setWhatsappConfirm] = useState("");
  const [ddiConfirm, setDdiConfirm] = useState("55");
  const [mismatch, setMismatch] = useState<{ first: string; second: string } | null>(null);
  const [mismatchCountdown, setMismatchCountdown] = useState(0);
  const [storeName, setStoreName] = useState("");
  const [storeSlug, setStoreSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [storeType, setStoreType] = useState("");

  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productImage, setProductImage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [serviceName, setServiceName] = useState("");
  const [servicePrice, setServicePrice] = useState("");
  const [serviceDuration, setServiceDuration] = useState("60");

  const [profName, setProfName] = useState("");
  const [profSchedule, setProfSchedule] = useState<ServiceSchedule>(() => {
    const gs: ServiceSchedule = {};
    dayKeys.forEach((k, i) => {
      gs[k] = { enabled: i < 5, ranges: [{ start: "08:00", end: "18:00" }] };
    });
    return gs;
  });

  const [registering, setRegistering] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [createdStore, setCreatedStore] = useState<any>(null);
  const [createdUser, setCreatedUser] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [confettiDone, setConfettiDone] = useState(false);

  const slugInputRef = useRef<HTMLInputElement>(null);
  const slugDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepContentRef = useRef<HTMLDivElement>(null);

  const storeData = (user as any)?.store;
  const userData = (user as any)?.user;
  const onboardingDone = storeData?.onboardingCompleted === true;

  const hasProducts = storeType === "produtos" || storeType === "ambos";
  const hasServices = storeType === "servicos" || storeType === "ambos";

  const getActualStep = () => {
    if (step <= 4) return step;
    let currentStep = 4;
    if (hasProducts) {
      currentStep++;
      if (step === currentStep) return 5;
    }
    if (hasServices) {
      currentStep++;
      if (step === currentStep) return 6;
      currentStep++;
      if (step === currentStep) return 7;
    }
    return 8;
  };

  const actualStep = getActualStep();

  // O «Receber link da minha loja» vai para o número do lojista no rodízio (Provedores em Rodízio, fase C). Busca quando o
  // último passo abre, para o toque não esperar a API antes de abrir o WhatsApp.
  const { data: numeroParaFalar } = useQuery<{ telefone: string | null; numeroId: string | null }>({
    queryKey: ["/api/whatsapp/numero-para-falar"],
    queryFn: () => customFetch("/api/whatsapp/numero-para-falar", { method: "GET" }),
    enabled: actualStep === 8,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (actualStep !== 8) return;
    const uid = userData?.id;
    if (!uid) return;
    if ((window as any).__vnzCrFired === uid) return;
    (window as any).__vnzCrFired = uid;
    trackCompleteRegistration(uid);
  }, [actualStep, userData?.id]);

  // Marca onboarding_completed=true assim que o lojista chega no step final.
  // Antes (jun/2026), isso só acontecia no clique do botão "Receber Link da
  // Minha Loja" — se ele fechasse o site antes, o magic link via OrganiZap
  // CRM autenticava OK mas o ProtectedRoute redirecionava de volta pro
  // /register de novo (loop). 67 lojistas ficaram presos. Dedup por uid
  // pra evitar POST duplicado se o useEffect re-rodar.
  useEffect(() => {
    if (actualStep !== 8) return;
    const uid = userData?.id;
    if (!uid) return;
    if ((window as any).__vnzOnboardingMarked === uid) return;
    (window as any).__vnzOnboardingMarked = uid;
    void customFetch("/api/auth/complete-onboarding", { method: "POST" })
      .then(() => {
        queryClient.setQueryData(getGetMeQueryKey(), (old: any) =>
          old ? { ...old, store: { ...old.store, onboardingCompleted: true }, user: { ...old.user, registerStep: null } } : old
        );
      })
      .catch(() => { /* best-effort — autocura no verify-magic-link cobre se falhar */ });
  }, [actualStep, userData?.id]);

  useEffect(() => {
    if (actualStep === 8 && !confettiDone) {
      setConfettiDone(true);
      const container = document.getElementById("confetti-container");
      if (!container) return;
      const colors = ["#f59e0b", "#10b981", "#3b82f6", "#ef4444", "#8b5cf6", "#ec4899"];
      for (let i = 0; i < 60; i++) {
        const piece = document.createElement("div");
        piece.style.cssText = `position:absolute;width:${6 + Math.random() * 6}px;height:${6 + Math.random() * 6}px;background:${colors[Math.floor(Math.random() * colors.length)]};left:${Math.random() * 100}%;top:-10px;border-radius:${Math.random() > 0.5 ? "50%" : "2px"};opacity:0.9;animation:confetti-fall ${1.5 + Math.random() * 2}s ease-out ${Math.random() * 0.5}s forwards;`;
        container.appendChild(piece);
      }
      const style = document.createElement("style");
      style.textContent = `@keyframes confetti-fall{0%{transform:translateY(0) rotate(0deg);opacity:1}100%{transform:translateY(100vh) rotate(${360 + Math.random() * 360}deg);opacity:0}}`;
      document.head.appendChild(style);
      setTimeout(() => { container.innerHTML = ""; style.remove(); }, 4000);
    }
  }, [actualStep, confettiDone]);

  const totalSteps = (() => {
    if (!storeType) return 4;
    let s = 4;
    if (hasProducts) s++;
    if (hasServices) s += 2;
    s++;
    return s;
  })();

  useEffect(() => {
    if (user && !onboardingDone && step === 1 && storeData) {
      setStoreType(storeData.storeType || "produtos");
      setCreatedStore(storeData);
      setCreatedUser(userData);
      const savedStep = userData?.registerStep;
      setStep(savedStep && savedStep >= 5 ? savedStep : 5);
    }
  }, [user, onboardingDone]);

  const persistStep = (nextStep: number) => {
    customFetch("/api/auth/update-register-step", {
      method: "POST",
      body: JSON.stringify({ step: nextStep }),
      headers: { "Content-Type": "application/json" },
    }).catch(() => {});
  };

  useEffect(() => {
    if (actualStep >= 8) return;
    const timer = setTimeout(() => {
      const el = stepContentRef.current?.querySelector<HTMLElement>("input, textarea, select");
      el?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [actualStep]);

  // ATENÇÃO: todos os hooks precisam ficar ACIMA dos early returns abaixo.
  // Hooks depois deles mudam a contagem entre renders (React error #310,
  // "Rendered more/fewer hooks") e derrubam a página inteira no ErrorBoundary.
  useEffect(() => {
    if (mismatchCountdown <= 0) return;
    const t = setTimeout(() => setMismatchCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [mismatchCountdown]);

  useEffect(() => {
    if (mismatch && mismatchCountdown === 0) {
      setMismatch(null);
      setConfirmingWhatsapp(false);
      setWhatsapp("");
      setWhatsappConfirm("");
      setWhatsappError("");
    }
  }, [mismatch, mismatchCountdown]);

  if (isLoading) {
    return <div className="h-[100svh] w-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  // Não redirecionar enquanto o lojista está no step final: o effect acima
  // marca onboarding_completed=true assim que o step 8 monta, e redirecionar
  // aqui arrancava ele da tela antes de ver o botão "Receber Link da Minha Loja".
  if (user && onboardingDone && actualStep !== 8) {
    return <Redirect to="/" />;
  }

  const handleStoreNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setStoreName(val);
    if (!slugManuallyEdited) {
      const newSlug = generateSlug(val);
      setStoreSlug(newSlug);
      setSlugAvailable(null);
      if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
      if (newSlug.length >= 2) {
        slugDebounceRef.current = setTimeout(() => checkSlugAvailability(newSlug), 1000);
      }
    }
  };

  const checkSlugAvailability = async (slug: string) => {
    if (!slug || slug.length < 2) { setSlugAvailable(null); return; }
    setCheckingSlug(true);
    try {
      const data = await customFetch(`/api/auth/check-slug/${slug}`, { method: "GET" });
      setSlugAvailable(data.available);
    } catch { setSlugAvailable(null); }
    finally { setCheckingSlug(false); }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await customFetch<{ url: string }>("/api/upload/image", {
        method: "POST",
        body: formData,
      });
      setProductImage(result.url);
    } catch (err) {
      toast({ title: "Erro ao enviar imagem", description: extractApiErrorMessage(err, "Tente novamente."), variant: "destructive" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const tracking = getTrackingData();
      const result = await customFetch<any>("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formatNameForSave(name),
          whatsappNumber: stripWhatsApp(whatsapp),
          ddi,
          storeName,
          storeSlug,
          storeType,
          reservedUserId: tracking.reservedUserId,
          adsWhatsapp: tracking.adsWhatsapp,
          adsWhatsappJid: tracking.adsWhatsappJid,
          country: tracking.country,
          state: tracking.state,
          city: tracking.city,
          zip: tracking.zip,
          // Fuso do navegador vira o default da loja (editável em Configurações).
          timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; } })(),
        }),
      });
      if (result.sessionId) {
        storeSessionId(result.sessionId);
        setSessionId(result.sessionId);
      }
      setCreatedStore(result.store);
      setCreatedUser(result.user);
      queryClient.setQueryData(getGetMeQueryKey(), { user: result.user, store: result.store });

      setStep(step + 1);
    } catch (error: any) {
      toast({
        title: "Erro ao registrar",
        description: extractApiErrorMessage(error, "Tente novamente mais tarde."),
        variant: "destructive",
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleValidateWhatsapp = async () => {
    setWhatsappError("");
    const stripped = stripWhatsApp(whatsapp);
    if (!stripped) { setWhatsappError("Informe o número de WhatsApp"); return; }
    setWhatsappValidating(true);
    try {
      const result = await customFetch<any>("/api/auth/validate-whatsapp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ number: stripped, ddi }),
      });
      if (result.alreadyRegistered) {
        // Brecha de auto-login removida (mai/2026). Mostra a tela
        // "já tem conta" pro lojista solicitar magic link via OrganiZap.
        setAlreadyRegistered({
          whatsappFormattedNumber: result.whatsappFormattedNumber || null,
        });
        return;
      }
      if (!result.valid) {
        setWhatsappError(result.error || "Este número não possui WhatsApp");
        return;
      }
      setWhatsappConfirm("");
      setDdiConfirm(ddi);
      setMismatch(null);
      setMismatchCountdown(0);
      setConfirmingWhatsapp(true);
    } catch (err: any) {
      setWhatsappError(err?.data?.message || "Erro ao validar WhatsApp");
    } finally {
      setWhatsappValidating(false);
    }
  };

  const handleConfirmWhatsapp = () => {
    const first = `${ddi}${stripWhatsApp(whatsapp)}`;
    const second = `${ddiConfirm}${stripWhatsApp(whatsappConfirm)}`;
    if (!stripWhatsApp(whatsappConfirm)) {
      setWhatsappError("Informe o número de WhatsApp");
      return;
    }
    if (first === second) {
      setConfirmingWhatsapp(false);
      setMismatch(null);
      setStep(3);
      return;
    }
    setMismatch({ first: whatsapp, second: whatsappConfirm });
    setMismatchCountdown(15);
  };

  const handleSaveProduct = async () => {
    if (!productName || !productPrice) { toast({ title: "Preencha nome e preço", variant: "destructive" }); return; }
    setSaving(true);
    try {
      await customFetch("/api/products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: productName,
          price: parseCurrency(productPrice),
          imageUrl: productImage,
          stock: null,
        }),
      });
      const nextStep = step + 1;
      setStep(nextStep);
      persistStep(nextStep);
    } catch { toast({ title: "Erro ao criar produto", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleSaveService = async () => {
    if (!serviceName || !servicePrice) { toast({ title: "Preencha nome e preço", variant: "destructive" }); return; }
    setSaving(true);
    try {
      await customFetch("/api/services", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: serviceName,
          price: parseCurrency(servicePrice),
          durationMinutes: parseInt(serviceDuration) || 60,
        }),
      });
      const nextStep = step + 1;
      setStep(nextStep);
      persistStep(nextStep);
    } catch { toast({ title: "Erro ao criar serviço", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleSaveProfessional = async () => {
    if (!profName) { toast({ title: "Informe o nome do profissional", variant: "destructive" }); return; }
    setSaving(true);
    try {
      await customFetch("/api/professionals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: profName,
          allServices: true,
          generalSchedule: profSchedule,
          isActive: true,
        }),
      });
      const nextStep = step + 1;
      setStep(nextStep);
      persistStep(nextStep);
    } catch { toast({ title: "Erro ao criar profissional", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const handleNext = async () => {
    if (actualStep === 1) {
      if (!name.trim()) { toast({ title: "Informe seu nome", variant: "destructive" }); return; }
      setStep(2);
    } else if (actualStep === 2) {
      if (confirmingWhatsapp) {
        handleConfirmWhatsapp();
      } else {
        await handleValidateWhatsapp();
      }
    } else if (actualStep === 3) {
      if (!storeName || !storeSlug) { toast({ title: "Preencha o nome e link da loja", variant: "destructive" }); return; }
      if (slugAvailable === false) { toast({ title: "Link indisponível", variant: "destructive" }); slugInputRef.current?.focus(); return; }
      if (slugAvailable === null) {
        await checkSlugAvailability(storeSlug);
        return;
      }
      setStep(4);
    } else if (actualStep === 4) {
      if (!storeType) { toast({ title: "Selecione o tipo de negócio", variant: "destructive" }); return; }
      await handleRegister();
    } else if (actualStep === 5) {
      await handleSaveProduct();
    } else if (actualStep === 6) {
      await handleSaveService();
    } else if (actualStep === 7) {
      await handleSaveProfessional();
    }
  };

  const minStep = (user && !onboardingDone) ? 5 : 1;

  const handleBack = () => {
    if (alreadyRegistered) {
      setAlreadyRegistered(null);
      setWhatsapp("");
      setWhatsappError("");
      return;
    }
    if (confirmingWhatsapp) {
      setConfirmingWhatsapp(false);
      setMismatch(null);
      setMismatchCountdown(0);
      return;
    }
    if (step > minStep && actualStep < 8) setStep(step - 1);
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const basePath = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  const catalogUrl = createdStore ? `${origin}${basePath}/loja/${createdStore.slug}` : "";
  const agendaUrl = createdStore ? `${origin}${basePath}/agendar/${createdStore.slug}` : "";

  const handleCopy = (url: string, key: string) => {
    navigator.clipboard.writeText(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );

  return (
    <div className="min-h-[100svh] w-full flex bg-background relative">
      <div id="confetti-container" className="fixed inset-0 pointer-events-none z-50 overflow-hidden" />
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-20 py-8 overflow-y-auto">
        <div ref={stepContentRef} className="max-w-[400px] w-full mx-auto animate-in slide-in-from-left duration-300 fade-in">

          {step > minStep && actualStep < 8 && !mismatch && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
          )}

          {actualStep === 1 && (
            <button
              onClick={() => setLocation("/login")}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
          )}

          {actualStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Como podemos te chamar?</h2>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name" className="mr-[0px] ml-[13px]">Nome Completo:</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(capitalizeWords(e.target.value))}
                  className="h-12 text-lg capitalize"
                  placeholder="Seu nome completo"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleNext(); } }}
                />
              </div>
            </div>
          )}

          {actualStep === 2 && !confirmingWhatsapp && !alreadyRegistered && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Qual seu WhatsApp?</h2>
                <p className="text-muted-foreground mt-2">Este será o número usado para login e receber pedidos.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp" className="ml-[13px]">WhatsApp da Loja: (para receber pedidos)</Label>
                <WhatsAppInput
                  id="whatsapp"
                  value={whatsapp}
                  onChange={(v: string) => { setWhatsapp(v); if (whatsappError) setWhatsappError(""); }}
                  ddi={ddi}
                  onDdiChange={setDdi}
                  className={`h-12 text-lg ${whatsappError ? "border-red-500 ring-1 ring-red-500" : ""}`}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleValidateWhatsapp(); } }}
                  required
                />
                {whatsappError && (
                  <p className="text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1">{whatsappError}</p>
                )}
              </div>
            </div>
          )}

          {actualStep === 2 && alreadyRegistered && codeRequest && (
            <CodeLoginCard
              ddi={codeRequest.ddi}
              whatsappNumber={codeRequest.whatsappNumber}
              whatsappFormattedNumber={codeRequest.whatsappFormattedNumber}
              requestId={codeRequest.requestId}
              onBack={() => setCodeRequest(null)}
            />
          )}

          {actualStep === 2 && alreadyRegistered && !codeRequest && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Este WhatsApp já tem conta</h2>
                <p className="text-muted-foreground mt-2">
                  Já existe uma loja cadastrada com o número:
                </p>
                <p className="font-mono text-lg font-semibold mt-1">
                  {alreadyRegistered.whatsappFormattedNumber || `+${ddi}${stripWhatsApp(whatsapp)}`}
                </p>
              </div>
              <Button
                type="button"
                className="w-full h-12 text-lg font-semibold group"
                disabled={magicLinkLoading}
                onClick={async () => {
                  setMagicLinkLoading(true);
                  setMagicLinkError("");
                  try {
                    const result = await requestCodeFlow({
                      whatsappNumber: stripWhatsApp(whatsapp),
                      ddi,
                      fallbackFormatted: alreadyRegistered.whatsappFormattedNumber,
                    });
                    if (result.status === "sent") {
                      setCodeRequest({
                        requestId: result.requestId,
                        whatsappFormattedNumber: result.whatsappFormattedNumber,
                        whatsappNumber: stripWhatsApp(whatsapp),
                        ddi,
                      });
                    } else {
                      setMagicLinkError("Não encontramos uma conta com este WhatsApp.");
                    }
                  } catch (err) {
                    setMagicLinkError(getApiErrorMessage(err, "Não foi possível enviar o código. Tente novamente."));
                  } finally {
                    setMagicLinkLoading(false);
                  }
                }}
              >
                {magicLinkLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando…
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5 mr-2" /> Receber código de acesso
                  </>
                )}
              </Button>
              {magicLinkLoading && (
                <p className="text-sm text-muted-foreground text-center" data-testid="aviso-codigo-30s">{CODIGO_CHEGA_EM_30S}</p>
              )}
              {magicLinkError && (
                <p className="text-sm text-red-500 font-medium text-center animate-in fade-in slide-in-from-top-1">{magicLinkError}</p>
              )}
              <button
                type="button"
                onClick={() => {
                  setAlreadyRegistered(null);
                  setWhatsapp("");
                  setWhatsappError("");
                  setMagicLinkError("");
                }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
              >
                Usar outro WhatsApp
              </button>
            </div>
          )}

          {actualStep === 2 && confirmingWhatsapp && !mismatch && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Repete WhatsApp</h2>
                <p className="text-muted-foreground mt-2">Digite seu WhatsApp mais uma vez</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsappConfirm" className="ml-[13px]">WhatsApp da Loja: (para receber pedidos)</Label>
                <WhatsAppInput
                  id="whatsappConfirm"
                  value={whatsappConfirm}
                  onChange={(v: string) => { setWhatsappConfirm(v); if (whatsappError) setWhatsappError(""); }}
                  ddi={ddiConfirm}
                  onDdiChange={setDdiConfirm}
                  className={`h-12 text-lg ${whatsappError ? "border-red-500 ring-1 ring-red-500" : ""}`}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleConfirmWhatsapp(); } }}
                  required
                />
                {whatsappError && (
                  <p className="text-sm text-red-500 font-medium animate-in fade-in slide-in-from-top-1">{whatsappError}</p>
                )}
              </div>
            </div>
          )}

          {actualStep === 2 && confirmingWhatsapp && mismatch && (
            <div className="space-y-8 animate-in fade-in duration-300">
              <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-6 space-y-4">
                <p className="text-lg text-red-600 font-bold leading-snug">
                  Você digitou 2 números de WhatsApp diferentes
                </p>
                <div className="text-base text-red-700 font-semibold space-y-1">
                  <p className="font-mono">{mismatch.first}</p>
                  <p className="font-mono">{mismatch.second}</p>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-6xl font-bold text-red-600 tabular-nums leading-none">
                  {mismatchCountdown}
                </span>
                <span className="text-sm text-muted-foreground">Voltando ao início...</span>
              </div>
            </div>
          )}

          {actualStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Dados da sua loja</h2>
                <p className="text-muted-foreground mt-2">Escolha o nome e o link que seus clientes usarão.</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="storeName">Nome da Loja</Label>
                  <Input
                    id="storeName"
                    placeholder="Ex: Hambúrgueria do João"
                    value={storeName}
                    onChange={handleStoreNameChange}
                    className="h-12 text-lg"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleNext(); } }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="storeSlug">Link da Loja</Label>
                  <div className="flex items-center">
                    <div className="h-12 px-3 bg-muted border border-border border-r-0 rounded-l-md flex items-center text-muted-foreground text-sm whitespace-nowrap">
                      {getPublicHost()}/loja/
                    </div>
                    <Input
                      id="storeSlug"
                      ref={slugInputRef}
                      value={storeSlug}
                      onChange={(e) => {
                        setSlugManuallyEdited(true);
                        const newSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
                        setStoreSlug(newSlug);
                        setSlugAvailable(null);
                        if (slugDebounceRef.current) clearTimeout(slugDebounceRef.current);
                        if (newSlug.length >= 2) {
                          slugDebounceRef.current = setTimeout(() => checkSlugAvailability(newSlug), 1000);
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleNext(); } }}
                      className={`h-12 rounded-l-none ${slugAvailable === false ? "border-red-500" : slugAvailable === true ? "border-green-500" : ""}`}
                    />
                  </div>
                  {slugAvailable === false && <p className="text-xs text-red-500 font-medium">Este link já está em uso. Escolha outro.</p>}
                  {slugAvailable === true && <p className="text-xs text-green-600 font-medium">Link disponível!</p>}
                  {checkingSlug && <p className="text-xs text-muted-foreground">Verificando disponibilidade...</p>}
                  {slugAvailable === null && !checkingSlug && <p className="text-xs text-muted-foreground">Este será o link que você enviará aos clientes.</p>}
                </div>
              </div>
            </div>
          )}

          {actualStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">O que você deseja oferecer?</h2>
                <p className="text-muted-foreground mt-2">Escolha o tipo do seu negócio.</p>
              </div>
              <div className="space-y-3">
                {[
                  { value: "produtos", label: "Produtos e Encomendas", desc: "restaurante, açaí, doces, bolos, marmitas, cosméticos, etc", icon: ShoppingBag },
                  { value: "servicos", label: "Serviços", desc: "salão de beleza, barbearia, estética, consultório, etc", icon: Calendar },
                  { value: "ambos", label: "Produtos e Serviços", desc: "loja e agenda juntas", icon: Layers },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStoreType(opt.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer flex items-center gap-4 ${
                      storeType === opt.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${storeType === opt.value ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                      <opt.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`font-semibold ${storeType === opt.value ? "text-primary" : ""}`}>{opt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                      {(opt as any).hint && storeType === opt.value && (
                        <p className="text-xs text-orange-600 mt-1.5 leading-relaxed">{(opt as any).hint}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {actualStep === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Seu primeiro produto</h2>
                <p className="text-muted-foreground mt-2">Adicione um produto para começar. Você pode adicionar mais depois.</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="pl-[13px]">Nome do Produto:</Label>
                  <Input value={productName} onChange={(e) => setProductName(e.target.value)} className="h-12" placeholder="Ex: Hambúrguer Artesanal" autoFocus onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveProduct(); } }} />
                </div>
                <div className="space-y-2">
                  <Label className="pl-[13px]">Preço (R$):</Label>
                  <Input
                    value={productPrice}
                    onChange={(e) => setProductPrice(formatCurrency(e.target.value))}
                    className="h-12"
                    placeholder="0,00"
                    inputMode="numeric"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveProduct(); } }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="pl-[13px]">Foto (opcional):</Label>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  {productImage ? (
                    <div className="relative w-full h-40 rounded-xl overflow-hidden border">
                      <img src={productImage} alt="Produto" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setProductImage(null)}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 cursor-pointer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="w-full h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                    >
                      {uploadingImage ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-8 h-8" />}
                      <span className="text-sm font-medium">{uploadingImage ? "Enviando..." : "Clique para adicionar foto"}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {actualStep === 6 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Seu primeiro serviço</h2>
                <p className="text-muted-foreground mt-2">Adicione um serviço para começar. Você pode adicionar mais depois.</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="pl-[13px]">Nome do Serviço:</Label>
                  <Input value={serviceName} onChange={(e) => setServiceName(e.target.value)} className="h-12" placeholder="Ex: Corte de Cabelo" autoFocus onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveService(); } }} />
                </div>
                <div className="space-y-2">
                  <Label className="pl-[13px]">Preço (R$):</Label>
                  <Input
                    value={servicePrice}
                    onChange={(e) => setServicePrice(formatCurrency(e.target.value))}
                    className="h-12"
                    placeholder="0,00"
                    inputMode="numeric"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveService(); } }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="pl-[13px]">Duração (minutos):</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-muted-foreground shrink-0" />
                    <select
                      value={serviceDuration}
                      onChange={(e) => setServiceDuration(e.target.value)}
                      className="flex-1 h-12 border rounded-md p-2 text-base bg-background"
                    >
                      {[15, 30, 45, 60, 90, 120, 150, 180, 240].map((m) => {
                        const h = Math.floor(m / 60);
                        const r = m % 60;
                        const label = m < 60 ? "" : ` (${h}h${r > 0 ? r.toString().padStart(2, "0") : ""})`;
                        return <option key={m} value={m}>{m} min{label}</option>;
                      })}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {actualStep === 7 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold tracking-tight">Primeiro profissional</h2>
                <p className="text-muted-foreground mt-2">Pode ser você mesmo! Defina o nome e os horários de atendimento.</p>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="pl-[13px]">Nome do Profissional:</Label>
                  <Input
                    value={profName}
                    onChange={(e) => setProfName(capitalizeWords(e.target.value))}
                    className="h-12"
                    placeholder="Ex: João Silva"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveProfessional(); } }}
                  />
                </div>

                <div className="space-y-3">
                  <Label className="pl-[13px]">Dias de atendimento:</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {dayKeys.map((dk, i) => {
                      const isEnabled = profSchedule[dk]?.enabled;
                      return (
                        <button
                          key={dk}
                          type="button"
                          onClick={() => {
                            setProfSchedule((prev) => {
                              const day = { ...(prev[dk] || { enabled: false, ranges: [{ start: "08:00", end: "18:00" }] }) };
                              day.enabled = !day.enabled;
                              if (day.enabled && day.ranges.length === 0) day.ranges = [{ start: "08:00", end: "18:00" }];
                              return { ...prev, [dk]: day };
                            });
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                            isEnabled ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"
                          }`}
                        >
                          {dayLabels[i]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {dayKeys.filter((dk) => profSchedule[dk]?.enabled).length > 0 && (
                  <div className="space-y-3">
                    {dayKeys.filter((dk) => profSchedule[dk]?.enabled).map((dk) => {
                      const dayIdx = dayKeys.indexOf(dk);
                      const dayData = profSchedule[dk]!;
                      return (
                        <div key={dk} className="border rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{dayLabels[dayIdx]}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setProfSchedule((prev) => {
                                  const day = { ...(prev[dk] || { enabled: true, ranges: [] }) };
                                  const last = day.ranges[day.ranges.length - 1];
                                  const lastEndIdx = last ? timeOptions.indexOf(last.end) : -1;
                                  const nextStart = lastEndIdx >= 0 && lastEndIdx < timeOptions.length - 1
                                    ? timeOptions[lastEndIdx + 1]
                                    : (last?.end || "13:00");
                                  const nextStartIdx = timeOptions.indexOf(nextStart);
                                  const nextEnd = nextStartIdx >= 0 && nextStartIdx < timeOptions.length - 1
                                    ? timeOptions[nextStartIdx + 1]
                                    : nextStart;
                                  day.ranges = [...day.ranges, { start: nextStart, end: nextEnd }];
                                  return { ...prev, [dk]: day };
                                });
                              }}
                              className="text-xs text-primary font-medium hover:underline cursor-pointer"
                            >
                              + Adicionar Horário
                            </button>
                          </div>
                          {dayData.ranges.map((range, ri) => {
                            const prev = ri > 0 ? dayData.ranges[ri - 1] : null;
                            const prevEndIdx = prev ? timeOptions.indexOf(prev.end) : -1;
                            const startOptions = prevEndIdx >= 0
                              ? timeOptions.slice(prevEndIdx + 1)
                              : timeOptions;
                            const startIdx = timeOptions.indexOf(range.start);
                            const endOptions = startIdx >= 0
                              ? timeOptions.slice(startIdx + 1)
                              : timeOptions;
                            return (
                              <div key={ri} className="flex items-center gap-2">
                                <select
                                  className="flex-1 border rounded-md p-2 text-sm bg-background"
                                  value={range.start}
                                  onChange={(e) => {
                                    setProfSchedule((prevState) => {
                                      const day = { ...(prevState[dk] || { enabled: true, ranges: [] }) };
                                      day.ranges = day.ranges.map((r, i) => {
                                        if (i !== ri) return r;
                                        const newStart = e.target.value;
                                        const ni = timeOptions.indexOf(newStart);
                                        const ei = timeOptions.indexOf(r.end);
                                        const newEnd = ei > ni
                                          ? r.end
                                          : (timeOptions[ni + 1] ?? timeOptions[timeOptions.length - 1]);
                                        return { ...r, start: newStart, end: newEnd };
                                      });
                                      return { ...prevState, [dk]: day };
                                    });
                                  }}
                                >
                                  {startOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                                <span className="text-xs text-muted-foreground">às</span>
                                <select
                                  className="flex-1 border rounded-md p-2 text-sm bg-background"
                                  value={range.end}
                                  onChange={(e) => {
                                    setProfSchedule((prevState) => {
                                      const day = { ...(prevState[dk] || { enabled: true, ranges: [] }) };
                                      day.ranges = day.ranges.map((r, i) => (i === ri ? { ...r, end: e.target.value } : r));
                                      return { ...prevState, [dk]: day };
                                    });
                                  }}
                                >
                                  {endOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                                {dayData.ranges.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setProfSchedule((prevState) => {
                                        const day = { ...(prevState[dk] || { enabled: true, ranges: [] }) };
                                        day.ranges = day.ranges.filter((_, i) => i !== ri);
                                        return { ...prevState, [dk]: day };
                                      });
                                    }}
                                    className="text-red-400 hover:text-red-600 cursor-pointer"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {actualStep === 8 && (
            <div className="space-y-8 text-center">
              <div className="space-y-4">
                <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto animate-in zoom-in duration-500">
                  <PartyPopper className="w-10 h-10" />
                </div>
                <h2 className="text-3xl font-bold tracking-tight">Sua loja está pronta!</h2>
                <p className="text-muted-foreground">Agora é só utilizar o link da<br />sua loja para receber pedidos<br /><br />Pegue o link dela no botão abaixo  👇</p>
              </div>

              {hasServices && agendaUrl && (
                <div className="space-y-2 text-left">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Agenda online</Label>
                  <div className="flex items-center gap-2">
                    <Input value={agendaUrl.replace(/^https?:\/\//, "")} readOnly className="text-sm font-mono flex-1 min-w-0" onClick={(e) => (e.target as HTMLInputElement).select()} />
                    <Button variant="outline" size="icon" className="shrink-0 cursor-pointer" onClick={() => handleCopy(agendaUrl, "agenda")}>
                      {copiedKey === "agenda" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/10 cursor-pointer"
                    onClick={() => {
                      const msg = `Agende online conosco: ${agendaUrl}`;
                      abreWhatsApp(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`);
                    }}
                  >
                    <WhatsAppIcon className="w-4 h-4 mr-2" /> Compartilhar no WhatsApp
                  </Button>
                </div>
              )}

              <Button
                className="w-full h-12 text-lg font-semibold group"
                onClick={async () => {
                  const formatted = userData?.whatsappFormattedNumber || (userData?.whatsappNumber ? `+${userData.whatsappNumber}` : "");
                  const text = `Receber link da minha loja\n${formatted}`;
                  // Sem resposta da API, vai o número do suporte.
                  const telefone = numeroParaFalar?.telefone?.replace(/\D/g, "") || "5511921048695";
                  const waUrl = `https://api.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(text)}`;
                  try {
                    await customFetch("/api/auth/complete-onboarding", { method: "POST" });
                    queryClient.setQueryData(getGetMeQueryKey(), (old: any) =>
                      old ? { ...old, store: { ...old.store, onboardingCompleted: true }, user: { ...old.user, registerStep: null } } : old
                    );
                  } catch (e) {
                    toast({ title: "Erro ao finalizar cadastro. Tente novamente.", variant: "destructive" });
                    return;
                  }
                  abreWhatsApp(waUrl);
                  setLocation(storeType === "servicos" ? "/servicos" : "/produtos");
                }}
              >
                Receber Link da Minha Loja <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          )}

          {actualStep < 8 && !mismatch && !alreadyRegistered && (
            <div className="flex items-center gap-3 mt-8">
              <Button
                className="w-full h-12 text-lg font-semibold group cursor-pointer"
                onClick={handleNext}
                disabled={registering || whatsappValidating || saving || checkingSlug}
              >
                {(registering || whatsappValidating || saving || checkingSlug) ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Continuar <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </div>
          )}

        </div>
      </div>
      <div className="hidden lg:block lg:w-1/2 relative bg-muted overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-bl from-accent/30 to-primary/20 mix-blend-overlay z-10" />
        <img
          src={`${import.meta.env.BASE_URL}images/login-hero.png`}
          alt="E-commerce illustration"
          className="absolute inset-0 w-full h-full object-cover object-left"
        />
        <div className="absolute inset-0 bg-background/40 backdrop-blur-[2px] z-10" />

        <div className="absolute inset-0 z-20 flex items-center justify-center p-12">
          <div className="bg-card/90 backdrop-blur-xl border border-white/20 p-8 rounded-3xl shadow-2xl max-w-md w-full">
            <h3 className="text-2xl font-bold mb-6">Por que escolher o Venda no Zap?</h3>
            <ul className="space-y-4">
              {[
                "Pedidos chegam no seu WhatsApp",
                "Sem taxas sobre suas vendas",
                "Loja online bonita e profissional",
                "Plano grátis",
                "Gestão de estoque e clientes"
              ].map((benefit, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="font-medium text-foreground">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
